import React, { useMemo } from 'react';

import {
    Button,
    Card,
    Skeleton,
    Table,
    Flex,
    Tooltip,
    Typography,
    theme,
    TableColumnsType,
} from '@app/ui';
import { useTranslation } from 'react-i18next';
import FormGroup from '@app/ui/sports-components/FormGroup';
import '@app/components/table/table.scss';
import './BroadcastDemographicsTable.scss';

import Empty from '@app/components/common/Empty';
import {
    AGE_BRACKET_LABEL_KEYS,
    DemographicsBreakdownRow,
    DemographicsShares,
    formatAudience,
    formatShare,
    getBreakdownShares,
    onExportDemographicsToCsv,
    sumBreakdownColumns,
} from './demographicsUtils';

interface BroadcastDemographicsTableProps {
    rows: DemographicsBreakdownRow[];
    csvDelimiter?: string | null;
    loading: boolean;
    filters?: any;
}

// A table line carries its shares so the column renderers stay pure.
type BreakdownLine = DemographicsBreakdownRow & { shares: DemographicsShares };

type ShareTone = 'default' | 'total';

// % cells are bold so they read as the qualified figure. Total % colour
// lives on `.demographics-share-total` so it is not overridden by the
// Typography / cssVar theme (which keeps resolving to Relo Blue).
const renderShare = (text: string, tone: ShareTone = 'default') => (
    <Typography.Text
        strong
        className={
            tone === 'total'
                ? 'demographics-share demographics-share-total'
                : 'demographics-share'
        }
    >
        {text}
    </Typography.Text>
);

// The gender split is bold; the parenthetical share of the grand total
// is not, so "53.5% (1.8%)" reads as a qualified figure plus a footnote.
const renderGenderShare = (
    withinGendered: number | null | undefined,
    ofTotal: number | null | undefined,
) => {
    if (ofTotal == null) {
        return renderShare(formatShare(null));
    }

    return (
        <span className="demographics-share">
            <Typography.Text strong>
                {formatShare(withinGendered)}
            </Typography.Text>{' '}
            <Typography.Text>({formatShare(ofTotal)})</Typography.Text>
        </span>
    );
};

const BroadcastDemographicsTable = ({
    rows = [],
    csvDelimiter,
    loading,
    filters,
}: BroadcastDemographicsTableProps) => {
    const { t } = useTranslation(['common', 'measurement']);
    const { token } = theme.useToken();

    const totals = useMemo(() => sumBreakdownColumns(rows), [rows]);
    const grandTotal = totals.total;

    const lines = useMemo<BreakdownLine[]>(
        () =>
            rows.map(row => ({
                ...row,
                shares: getBreakdownShares(row, grandTotal),
            })),
        [rows, grandTotal],
    );

    const columns = useMemo<TableColumnsType<BreakdownLine>>(() => {
        const column = (key: string) =>
            t(`measurement:broadcastExposure.demographics.columns.${key}`);
        const countTitle = column('count');
        const shareTitle = column('share');

        // The top header row is shaded by the co-located stylesheet; see
        // `.demographics-group-header` and the CSS variable set on the wrapper.
        const groupHeaderCell = () => ({
            className: 'demographics-group-header',
        });
        // Sub-headers are centred over their right-aligned numbers.
        const subHeaderCell = () => ({
            style: { textAlign: 'center' as const },
        });

        return [
            {
                title: column('ageBracket'),
                dataIndex: 'ageBracket',
                key: 'ageBracket',
                onHeaderCell: groupHeaderCell,
                render: (value: DemographicsBreakdownRow['ageBracket']) =>
                    t(AGE_BRACKET_LABEL_KEYS[value]),
            },
            {
                title: (
                    <Flex align="center" justify="center" gap={6}>
                        {column('total')}
                        <Tooltip
                            title={t(
                                'measurement:broadcastExposure.demographics.totalNote',
                            )}
                        >
                            <i
                                className="fas fa-info-circle"
                                data-test="demographics-total-note"
                                style={{ color: token.colorPrimary }}
                            />
                        </Tooltip>
                    </Flex>
                ),
                key: 'total',
                onHeaderCell: groupHeaderCell,
                children: [
                    {
                        title: countTitle,
                        dataIndex: 'total',
                        key: 'totalCount',
                        align: 'right',
                        onHeaderCell: subHeaderCell,
                        render: formatAudience,
                    },
                    {
                        title: shareTitle,
                        key: 'totalShare',
                        align: 'right',
                        onHeaderCell: subHeaderCell,
                        render: (_: unknown, { shares }: BreakdownLine) =>
                            renderShare(formatShare(shares.total), 'total'),
                    },
                ],
            },
            {
                title: column('male'),
                key: 'male',
                onHeaderCell: groupHeaderCell,
                children: [
                    {
                        title: countTitle,
                        dataIndex: 'male',
                        key: 'maleCount',
                        align: 'right',
                        onHeaderCell: subHeaderCell,
                        render: formatAudience,
                    },
                    {
                        title: shareTitle,
                        key: 'maleShare',
                        align: 'right',
                        onHeaderCell: subHeaderCell,
                        render: (_: unknown, { shares }: BreakdownLine) =>
                            renderGenderShare(shares.male, shares.maleOfTotal),
                    },
                ],
            },
            {
                title: column('female'),
                key: 'female',
                onHeaderCell: groupHeaderCell,
                children: [
                    {
                        title: countTitle,
                        dataIndex: 'female',
                        key: 'femaleCount',
                        align: 'right',
                        onHeaderCell: subHeaderCell,
                        render: formatAudience,
                    },
                    {
                        title: shareTitle,
                        key: 'femaleShare',
                        align: 'right',
                        onHeaderCell: subHeaderCell,
                        render: (_: unknown, { shares }: BreakdownLine) =>
                            renderGenderShare(
                                shares.female,
                                shares.femaleOfTotal,
                            ),
                    },
                ],
            },
        ];
    }, [t, token.colorPrimary]);

    const summary = () => {
        if (!rows.length) {
            return null;
        }
        const shares = getBreakdownShares(totals, grandTotal);
        // The footer shows the gender split only. Its share of the grand
        // total adds nothing at this level, so no parenthetical here.
        const cells = [
            formatAudience(totals.total),
            formatShare(shares.total),
            formatAudience(totals.male),
            formatShare(shares.male),
            formatAudience(totals.female),
            formatShare(shares.female),
        ];
        // cells[1] is Total % — bold + blue, matching the body column.
        const TOTAL_SHARE_INDEX = 1;

        return (
            <Table.Summary.Row>
                <Table.Summary.Cell
                    index={0}
                    className="summary-cell first-cell"
                >
                    <Typography.Text strong>
                        {t('measurement:broadcastExposure.table.total')}
                    </Typography.Text>
                </Table.Summary.Cell>
                {cells.map((value, index) => (
                    <Table.Summary.Cell
                        key={index}
                        index={index + 1}
                        align="right"
                        className={
                            index === cells.length - 1
                                ? 'summary-cell last-cell'
                                : 'summary-cell'
                        }
                    >
                        {index === TOTAL_SHARE_INDEX ? (
                            renderShare(value, 'total')
                        ) : (
                            <Typography.Text strong>{value}</Typography.Text>
                        )}
                    </Table.Summary.Cell>
                ))}
            </Table.Summary.Row>
        );
    };

    return (
        <Card
            style={{
                margin: '24px 8px 0 8px',
            }}
        >
            <Flex
                align="center"
                style={{
                    marginBottom: 24,
                }}
            >
                <Flex vertical flex={1} gap={4}>
                    <Typography.Title
                        level={4}
                        style={{
                            margin: 0,
                            color: token.colorText,
                            fontWeight: 400,
                        }}
                    >
                        {t(
                            'measurement:broadcastExposure.demographics.cardTitle',
                        )}
                    </Typography.Title>
                    <Typography.Text
                        type="secondary"
                        data-test="demographics-share-note"
                    >
                        {t(
                            'measurement:broadcastExposure.demographics.shareNote',
                        )}
                    </Typography.Text>
                </Flex>

                <Flex>
                    <FormGroup>
                        <Button
                            onClick={() =>
                                onExportDemographicsToCsv(
                                    rows,
                                    csvDelimiter || ',',
                                    t,
                                    filters,
                                )
                            }
                            disabled={loading || rows.length === 0}
                            data-test="demographics-download"
                            type="primary"
                            color="primary"
                            variant="solid"
                        >
                            <i className="fa-regular fa-download -m-r-1" />
                            {t(
                                'measurement:broadcastExposure.table.downloadCsv',
                            )}
                        </Button>
                    </FormGroup>
                </Flex>
            </Flex>
            <div
                className="demographics-table"
                data-test="demographics-table-container"
                style={
                    {
                        '--demographics-header-shade': token.colorFill,
                    } as React.CSSProperties
                }
            >
                <Table
                    rowKey="ageBracket"
                    columns={columns}
                    dataSource={lines}
                    loading={loading}
                    pagination={false}
                    bordered
                    locale={{
                        emptyText: loading ? (
                            <Skeleton active paragraph={{ rows: 7 }} />
                        ) : (
                            <Empty />
                        ),
                    }}
                    summary={summary}
                />
                <Typography.Text
                    className="demographics-coverage-note"
                    data-test="demographics-coverage-note"
                >
                    {t(
                        'measurement:broadcastExposure.demographics.coverageNote',
                    )}
                </Typography.Text>
            </div>
        </Card>
    );
};

export default BroadcastDemographicsTable;
